import { CircularProgress } from '@civilgrid/ui/components';
import {
  type Cell,
  type ColumnDef as ColumnDefBase,
  flexRender,
  type Row,
  type RowData,
  type Table as TableType,
} from '@tanstack/react-table';
import classNames from 'classnames';
import { debounce, isEmpty } from 'lodash';
import type React from 'react';
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useMeasure } from 'react-use';
import Icon from '../Icon';
import GlobalFilter from './GlobalFilter';
import { dateTimeSortingFn } from './helpers';
import Pagination, { type PaginationProps } from './Pagination';
import SearchAsync, { type SearchAsyncColumnDef, type SearchAsyncProps, type SearchRef } from './SearchAsync';
import { TableV2 } from './TableV2';
import type { ExportableColumnDef } from './types';

export const AUTO_COL_WIDTH = 999;

export type ColumnDef<TData extends RowData, TValue = any> = ColumnDefBase<TData, TValue> & {
  cellClassName?: string | ((cell: Cell<TData, TValue>) => string);
  columns?: ColumnDef<TData, TValue>[];
  sortIconPosition?: 'right' | 'left';
};

export {
  GlobalFilter,
  Pagination,
  type PaginationProps,
  SearchAsync,
  type SearchAsyncProps,
  type SearchAsyncColumnDef,
  type SearchRef,
  type ExportableColumnDef,
  dateTimeSortingFn,
  TableV2,
};

export interface TableProps<TData, TValue> {
  id: string; // unique ID for each table. This is used for persisting state.
  table: TableType<TData>;
  onRowClick?: (row: Row<TData>) => void;
  onRowDoubleClick?: (row: Row<TData>) => void;
  onCellClick?: (cell: Cell<TData, TValue>) => void;
  getRowClassName?: (row: Row<TData>) => string;
  evenOdd?: boolean;
  isLoading?: boolean;
  topSection?: ReactNode;
}

export default function Table<TData = any[], TValue = any>({
  id,
  table,
  onRowClick,
  onRowDoubleClick,
  onCellClick,
  getRowClassName,
  evenOdd = true,
  isLoading: _isDataLoading,
  topSection,
}: TableProps<TData, TValue>) {
  const [isAutoResizing, setIsAutoResizing] = useState(true);
  const [tableParentRef, parentDims] = useMeasure<HTMLDivElement>();
  const tableHeaderRef = useRef<HTMLDivElement>(null);
  const [topSectionRef, topSectionDims] = useMeasure<HTMLDivElement>();
  const { columnSizing } = table.getState();

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  useEffect(() => {
    if (parentDims.width === 0) {
      return; // not done loading yet
    }
    // load saved widths first
    const savedWidths = localStorage.getItem(`table-column-size-state-${id}`);
    if (savedWidths) {
      table.setColumnSizing(JSON.parse(savedWidths));
    }

    const allColumns = table.getAllColumns();
    let totalAvailable = parentDims.width;
    const colsWithoutSize: string[] = [];
    allColumns.forEach((c) => {
      if (c.getSize() === AUTO_COL_WIDTH) {
        colsWithoutSize.push(c.id);
      } else {
        totalAvailable = totalAvailable - c.getSize();
      }
    });

    if (colsWithoutSize.length > 0) {
      if (totalAvailable > 0) {
        const sizePerCol = totalAvailable / colsWithoutSize.length;
        const newSizing: any = {};
        colsWithoutSize.forEach((c) => {
          newSizing[c] = sizePerCol;
        });

        table.setColumnSizing(newSizing);
      }
    }
    setIsAutoResizing(false);
  }, [parentDims.width]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  const debouncedSave = useMemo(
    () =>
      debounce((columnSizing) => {
        localStorage.setItem(`table-column-size-state-${id}`, JSON.stringify(columnSizing));
      }, 100),
    [],
  );

  useEffect(() => {
    if (!isEmpty(columnSizing)) {
      debouncedSave(columnSizing);
    }
  }, [columnSizing, debouncedSave]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  useEffect(() => {
    const sortState = sessionStorage.getItem(`table-sort-state-${id}`);
    if (sortState) {
      table.setSorting(JSON.parse(sortState));
    }
  }, []);

  let contents = <CircularProgress indeterminate />;

  const isLoading = isAutoResizing || _isDataLoading;

  if (!isLoading) {
    contents = (
      <>
        <div ref={tableHeaderRef} className="sticky z-10 overflow-x-hidden" style={{ top: topSectionDims.height }}>
          {table.getHeaderGroups().map((headerGroup, i) => {
            return (
              <div key={headerGroup.id} className={`inline-flex overflow-visible border-0 bg-secondary-light`}>
                {headerGroup.headers.map((header, j) => {
                  const isResizable =
                    header.getContext().table.options.enableColumnResizing && header.column.getCanResize();

                  let headerSortIcon: React.ReactNode = null;

                  if (header.column.getCanSort()) {
                    headerSortIcon = <div className="h-4 w-4"></div>;

                    if (header.column.getIsSorted() === 'asc') {
                      headerSortIcon = <Icon icon="arrowUp" />;
                    } else if (header.column.getIsSorted() === 'desc') {
                      headerSortIcon = <Icon icon="arrowDown" />;
                    }
                  }

                  let headerSortIconPosition = 'right';
                  if ((header.column.columnDef as ColumnDef<TData>)?.sortIconPosition === 'left') {
                    headerSortIconPosition = 'left';
                  }

                  return (
                    <span
                      key={header.id}
                      style={{
                        width: Math.max(50, header.getSize()),
                      }}
                      className={classNames('relative inline-block overflow-hidden border-secondary px-2 py-1', {
                        'border-b-[0.5px]': true,
                        'border-l-[0.5px]': j === 0,
                        'border-r-[0.5px]': true,
                        '!border-b-0 ': header.isPlaceholder,
                        'border-t-[0.5px]': headerGroup.depth === 0,
                      })}
                    >
                      <div
                        className={classNames('flex h-full items-center ', {
                          'cursor-pointer': header.column.getCanSort() && !header.isPlaceholder,
                        })}
                        onClick={() => {
                          if (table.options.enableSorting && header.column.getCanSort() && !header.isPlaceholder) {
                            const sort = header.column.getIsSorted() === 'asc';
                            header.column.toggleSorting(sort);

                            sessionStorage.setItem(
                              `table-sort-state-${id}`,
                              JSON.stringify([{ id: header.column.id, desc: sort }]),
                            );
                          }
                        }}
                      >
                        {headerSortIconPosition === 'left' &&
                          table.options.enableSorting &&
                          !header.isPlaceholder &&
                          headerSortIcon}
                        <div
                          className={classNames('flex h-full w-full place-content-start items-center font-medium', {
                            'justify-center': false,
                          })}
                        >
                          {header.isPlaceholder ? (
                            <div>&nbsp;</div>
                          ) : (
                            flexRender(header.column.columnDef.header, header.getContext())
                          )}
                        </div>
                        {headerSortIconPosition === 'right' &&
                          table.options.enableSorting &&
                          !header.isPlaceholder &&
                          headerSortIcon}
                      </div>

                      {isResizable && header.getContext().table.options.columnResizeMode && (
                        <div
                          onMouseDown={(e) => {
                            header.getResizeHandler()(e);
                          }}
                          className={`absolute right-0 top-0 h-full w-2 cursor-col-resize select-none hover:bg-secondary/50`}
                        />
                      )}
                    </span>
                  );
                })}
              </div>
            );
          })}
        </div>
        <div
          onScroll={(e) => {
            tableHeaderRef.current?.scrollTo({
              left: (e.target as HTMLDivElement).scrollLeft,
            });
          }}
          className="overflow-x-auto"
        >
          {table.getRowModel().rows.map((row, rowIndex) => {
            const className = getRowClassName?.(row) || '';
            return (
              <div key={row.id}>
                <div
                  id={`row-${row.id}`}
                  onClick={() => {
                    onRowClick?.(row);
                  }}
                  onDoubleClick={() => {
                    onRowDoubleClick?.(row);
                  }}
                  className={classNames(
                    'inline-flex overflow-visible hover:bg-secondary-light/50',
                    className,
                    { 'cursor-pointer': onRowClick },
                    { 'bg-secondary-light/30': evenOdd && rowIndex % 2 === 0 },
                  )}
                >
                  {row.getVisibleCells().map((cell) => {
                    const cn = (cell.column.columnDef as ColumnDef<TData>).cellClassName;
                    let cellClassNames = '';

                    if (cn) {
                      cellClassNames = cn instanceof Function ? cn(cell) : cn;
                    }

                    return (
                      <span
                        key={cell.id}
                        className={classNames('overflow-hidden border-[0.5px] px-2 py-1', cellClassNames)}
                        style={{ width: Math.max(50, cell.column.getSize()) }}
                        onClick={(e) => {
                          if (onCellClick) {
                            e.stopPropagation();
                            onCellClick(cell as Cell<TData, TValue>);
                          }
                        }}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </span>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </>
    );
  }

  return (
    <div className="">
      <div className="sticky top-0 z-10 w-full bg-white" ref={topSectionRef}>
        {topSection}
      </div>
      <div ref={tableParentRef} className="block w-full">
        {contents}
      </div>
    </div>
  );
}
